import React, { useRef, useState } from 'react'
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'
import {
  CertCheckState,
  TermsAgreeState,
  MvmoTermsState,
  MvnoCheckValue,
  RecoilStateList,
  termsModalOpen,
  TimeoutState,
  TransIdState,
  VulnerableCheckState,
} from 'src/recoil/common'
import { formatPhoneNumber, redirectToStore } from 'src/service/utils'
import useAPI from './useAPI'
import { useLocalModal } from 'src/components/views/mkt04/Modal/provider'
import { useMVNOModal } from 'src/components/views/mkt04/KTMVNOModal'
import Lottie from 'lottie-react'
import ArrowLottie from 'src/assets/images/mkt04/arrow.json'
import { useFormContext } from 'react-hook-form'
import { JoinFormType } from 'src/types/type'
import { join_complete_greenp } from 'src/service/utils/greenp'
import { useTrackingPage } from 'src/hooks/useTrackingPage'
import { useModalWithPromise } from 'src/hooks/useModal'
import { already_signup_dataLayer, join_complete_dataLayer } from 'src/service/utils/gtmDatalayer'
import { captureMvnoTermsError } from 'src/service/utils/sentry/validation'

type SubmitOpts = { openToast?: (msg: string) => void }

const useSubmitWithModal = (pCode: string, opts?: SubmitOpts) => {
  const openToast = opts?.openToast
  const mvnoCheckValue = useRecoilValue(MvnoCheckValue)
  const setTermsAgree = useSetRecoilState(TermsAgreeState)
  const setTranId = useSetRecoilState(TransIdState)
  const setTimeoutState = useSetRecoilState(TimeoutState)
  const setCertCheck = useSetRecoilState(CertCheckState)
  const { TelType, PhoneNum, CertNum, AllTermsAgree, termsAgree, TransId, TermsData } =
    useRecoilValue(RecoilStateList)
  const setTermsModalOpen = useSetRecoilState(termsModalOpen)
  const setMvnoTerms = useSetRecoilState(MvmoTermsState)
  const setVulnerableCheck = useSetRecoilState(VulnerableCheckState)
  const ResetTermsAgree = useResetRecoilState(TermsAgreeState)
  const { openModal, closeModal, openModalAfterKeyboardClose, closeModalWithFocus } =
    useLocalModal()
  const { openMVNOModal } = useMVNOModal()
  const vulnerableModal = useModalWithPromise()
  const { isGreenpTracking } = useTrackingPage()
  const [joinLoading, setJoinLoading] = useState(false)
  const joinLockRef = useRef(false)
  const { setFocus } = useFormContext<JoinFormType>()

  const { mutateAsync: CheckMvmoSubmit } = useAPI('CheckMvnoMember').useSetMutation({
    Telecom: TelType,
    PNumber: PhoneNum,
  })
  const { mutateAsync: CheckVulnerableSubmit } = useAPI('CheckVulnerable').useSetMutation({
    Telecom: TelType,
    PNumber: PhoneNum,
  })
  const checkKTPassPCode = (pcode: string) =>
    ['ktpass_01', 'ktpass_02', 'ktpass_03'].includes(pcode)

  const termsStatus = (): Promise<boolean> => {
    return new Promise((resolve) => {
      const arrCopy = [...termsAgree]

      if (!AllTermsAgree) {
        if (checkKTPassPCode(pCode)) {
          setTermsModalOpen(true)
          resolve(true)
        } else {
          openModal({
            placement: 'center',
            width: '100%',
            maxWidth: 300,
            contents: {
              title: '서비스 이용을 위해 약관 동의가 필요해요',
              desc: '전체(선택 미동의) 동의 후 인증번호가 발송돼요.',
            },
            actions: {
              cancel: {
                text: '취소',
                func: () => {
                  closeModal()
                  resolve(false)
                },
              },
              submit: {
                text: '확인',
                icon: <Lottie animationData={ArrowLottie} loop autoplay />,
                iconSize: 26,
                func: () => {
                  closeModalWithFocus()
                  TermsData?.Terms?.forEach((terms) => {
                    if (terms.Order !== undefined) {
                      arrCopy[terms.Order - 1] = terms.Required ?? 0
                    }
                  })
                  setTermsAgree(arrCopy)
                  resolve(true)
                },
              },
            },
          })
        }
      } else {
        resolve(true)
      }
    })
  }

  const setMvnoTermsAgree = async (flag: number) => {
    if (TelType === 1 && mvnoCheckValue) {
      await new Promise((resolve) => {
        setTermsAgree((prev) => {
          const termsAgreeCopy = [...prev]
          termsAgreeCopy[8] = flag
          resolve(termsAgreeCopy)
          return termsAgreeCopy
        })
      })
    }
  }

  const checkVulnerableMember = async () => {
    const data = await CheckVulnerableSubmit()
    const { Vulnerable, AgePopUpYN } = data.Body

    if (Vulnerable === 'Y' || AgePopUpYN === 'Y') {
      setVulnerableCheck(true)
      // 모달 대기 중에는 로딩 오버레이를 감춘다. (ref 락은 계속 유지)
      setJoinLoading(false)
      const userConfirmed = await vulnerableModal.open()
      if (userConfirmed) setJoinLoading(true)
      return userConfirmed
    }

    return true
  }
  const validationCert = async (query: any, options?: { resend?: boolean }) => {
    const isResend = !!options?.resend

    if (PhoneNum.length !== 11) {
      await openModalAfterKeyboardClose({
        width: '100%',
        maxWidth: 300,
        contents: { title: '휴대폰번호를 정확하게 입력하여 주십시오.' },
        actions: { submit: { text: '확인', func: () => closeModal() } },
      })
      return false
    }

    if (!isResend) {
      const termsAccepted = await termsStatus()
      if (!termsAccepted) return false

      if (TelType === 1) {
        const data = await CheckMvmoSubmit()
        captureMvnoTermsError(data?.Body)
        if (data?.Body?.Type === 1) {
          if (mvnoCheckValue) {
            return await cert(query)
          } else {
            setMvnoTerms({ Terms: data.Body.Terms, TermsTitle: data.Body.TermsTitle })
            openMVNOModal(cert, query)
            return false
          }
        } else {
          return await cert(query)
        }
      } else {
        return await cert(query)
      }
    } else {
      if (TelType === 1) {
        const data = await CheckMvmoSubmit()
        if (data?.Body?.Type === 1 && !mvnoCheckValue) {
          setMvnoTerms({ Terms: data.Body.Terms, TermsTitle: data.Body.TermsTitle })
          openMVNOModal(recert, query)
          return false
        }
      }
      return await recert(query)
    }
  }

  const hardReload = (delay = 100) => {
    const doReload = () => {
      const w = window.top ?? window
      try {
        w.location.reload()
        return
      } catch {}
      try {
        w.location.replace(w.location.href)
        return
      } catch {}
      try {
        // eslint-disable-next-line no-self-assign
        w.location.href = w.location.href
        return
      } catch {}
    }
    setTimeout(doReload, delay)
  }

  const recert = async (query: any): Promise<boolean> => {
    try {
      const data = await query()
      if (data?.Header?.ErrCode === 0) {
        setCertCheck(false)
        await setTimeoutState(data?.Body?.TimeOut)
        setTranId(data?.Body?.TranID)
        setCertCheck(true)
        openToast?.('인증번호가 재발송되었습니다.')

        setTimeout(() => {
          setFocus('ApprovalNumber', { shouldSelect: true })
        }, 500)

        const prevOnFocus = window.onfocus
        window.onfocus = () => {
          setTimeout(() => {
            setFocus('ApprovalNumber', { shouldSelect: true })
          }, 200)
          window.onfocus = prevOnFocus || null
        }

        return true
      }
      handleApiError(data?.Header, data?.Body)
      return false
    } catch (e) {
      console.error(e)
      return false
    }
  }

  const cert = async (query: () => Promise<any>): Promise<boolean> => {
    try {
      const data: any = await query()
      const code = data?.Header?.ErrCode

      if (code === 0) {
        setCertCheck(false)
        await setTimeoutState?.(data?.Body?.TimeOut)
        setTranId(data?.Body?.TranID)
        setCertCheck(true)

        if (AllTermsAgree) {
          setTimeout(() => {
            setFocus('ApprovalNumber', { shouldSelect: true })
          }, 1000)

          const prevOnFocus = window.onfocus
          window.onfocus = () => {
            setTimeout(() => {
              setFocus('ApprovalNumber', { shouldSelect: true })
            }, 150)
            window.onfocus = prevOnFocus || null
          }
        }

        return true
      }

      if (code === 1021) {
        await openModalAfterKeyboardClose({
          width: '100%',
          maxWidth: 300,
          contents: { title: data?.Header?.ErrMsg },
          actions: {
            submit: {
              text: '확인',
              func: () => {
                closeModal()
                window.location.reload()
              },
            },
          },
        })
        return false
      }

      handleApiError(data?.Header, data?.Body)
      return false
    } catch (error) {
      console.error(error)
      return false
    }
  }

  const handleApiError = (header: any, body: any) => {
    const errorMessage =
      header?.ErrMsg ?? `(${header?.CmdType}) ${header?.ErrCode} : 알 수 없는 오류가 발생했습니다.`

    switch (header?.ErrCode) {
      case 1008:
        openModalAfterKeyboardClose({
          width: '100%',
          maxWidth: 300,
          contents: { title: errorMessage },
          actions: {
            submit: {
              text: '확인',
              func: () => {
                closeModal()
                redirectToStore()
              },
            },
          },
        })
        break
      case 1006:
      case 1031:
      case 1033:
      case 1036:
      case 1040:
      case 9000:
      case 9001:
        openModalAfterKeyboardClose({
          width: '100%',
          maxWidth: 300,
          contents: { title: errorMessage },
          actions: {
            submit: {
              text: '확인',
              func: () => {
                closeModal()
                hardReload(120)
              },
            },
          },
        })
        break
      default:
        openModal({
          width: '100%',
          maxWidth: 300,
          contents: { title: errorMessage },
          actions: {
            submit: {
              text: '확인',
              func: () => closeModal(),
            },
          },
        })
        break
    }
  }
  const join = async (query: any) => {
    if (!AllTermsAgree) {
      await termsStatus()
      return
    }

    if (TransId === '') {
      await openModalAfterKeyboardClose({
        width: '100%',
        maxWidth: 300,
        contents: {
          title: '인증번호 받기를 먼저 시도하여 주세요.',
        },
        actions: {
          submit: {
            text: '확인',
            func: () => closeModal(),
          },
        },
      })
      return false
    }

    if (CertNum.length !== 4) {
      await openModalAfterKeyboardClose({
        width: '100%',
        maxWidth: 300,
        contents: {
          title: '인증번호를 입력하여 주세요.',
        },
        actions: {
          submit: {
            text: '확인',
            func: () => closeModal(),
          },
        },
      })
      return false
    }

    // 선행 확인 API(CheckVulnerable) 구간까지 포함해 중복 제출을 막는다.
    if (joinLockRef.current) return false
    joinLockRef.current = true
    setJoinLoading(true)
    try {
      await setMvnoTermsAgree(1)

      const joinConfirmed = await checkVulnerableMember()
      if (!joinConfirmed) return false

      const data: any = await query()

      if (data?.Header?.ErrCode === 1008) {
        already_signup_dataLayer()
        await openModalAfterKeyboardClose({
          width: '100%',
          maxWidth: 300,
          contents: { title: '이미 서비스에 가입되어 있습니다.' },
          actions: {
            submit: {
              text: '확인',
              func: () => {
                closeModal()
                redirectToStore()
              },
            },
          },
        })
        return false
      }
      if (data?.Header?.ErrCode === 0) {
        join_complete_dataLayer(PhoneNum)
        if (isGreenpTracking) join_complete_greenp(TransId)
        const formattedPhone = formatPhoneNumber(PhoneNum)
        window.gtag('event', 'conversion', {
          send_to: 'AW-10905019081/SJyqCOOH1r4DEMnF9c8o',
          user_data: {
            phone_number: formattedPhone,
          },
        })
        window.gtag('event', 'conversion', {
          send_to: 'AW-11453155594/3cWJCKCczIUZEIqSpdUq',
          user_data: {
            phone_number: formattedPhone,
          },
        })
        await openModalAfterKeyboardClose({
          width: '100%',
          maxWidth: 300,
          contents: {
            title: '휴대폰가족보호 안내',
            desc: '휴대폰을 통해 가족 구성원의 상태를 확인하고 도움이 필요한 상황을 알려 안전하게 보호받을 수 있는 안심 유료부가서비스에 가입되셨습니다. 원활한 이용을 위해 [확인] 버튼을 눌러 앱스토어로 이동하세요.',
          },
          actions: {
            submit: {
              text: '확인',
              func: () => {
                closeModal()
                redirectToStore()
              },
            },
          },
        })
        ResetTermsAgree()
        return true
      }
      handleApiError(data?.Header, data?.Body)
      await setMvnoTermsAgree(0)
      return false
    } catch (error: any) {
      const errMsg = error?.response?.data?.Header?.ErrMsg || '네트워크 오류가 발생했습니다.'
      await openModalAfterKeyboardClose({
        contents: { title: errMsg },
        actions: { submit: { text: '확인', func: () => closeModal() } },
      })
      return false
    } finally {
      setJoinLoading(false)
      joinLockRef.current = false
    }
  }
  return {
    cert,
    recert,
    join,
    validationCert,
    openModal, // 기본 모달
    openModalAfterKeyboardClose, // 키패드 닫고 모달
    closeModal,
    vulnerableModal,
    joinLoading,
  }
}
export default useSubmitWithModal
